import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'react-bootstrap';
import { SIZE } from 'baseui/button';
import store from 'store';
import { paths } from 'frameworks_drivers/routes';
import { TrackerUtil } from 'tracking/TrackerUtil';
import TitleWithPrevPageLink from 'presentation/components/TitleWithPrevPageLink';
import { setCentralHeatingAvailable as setCentralHeatingAvailableInStore } from 'frameworks_drivers/redux/reducers/onboardingInformation/onboardingInformationSlice';
import IconRadioGroup from 'presentation/components/IconRadioGroup';
import { RadioOptionKeys, translationKeyForRadioOption } from 'tools/RadioOptionMapper';
import option1Icon from 'presentation/assets/icons/png/3x/icn-ja@3x.png';
import option2Icon from 'presentation/assets/icons/png/3x/icn-nein@3x.png';
import { Button } from '@techem/ui-components';
import PageDescription from 'presentation/components/PageDescription';
import { WizardSteps, WizardStepsContainer } from 'presentation/components/WizardSteps';
import { useNavigate } from 'react-router-dom';
import { DISABLE_FLOW_DEEP_LINKING_PROTECTION } from 'config';
import useHotKey from 'tools/hooks/useHotKey';

const CentralHeatingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const prevPath = paths.ThermalMetering;

  useEffect(() => {
    if (
      !DISABLE_FLOW_DEEP_LINKING_PROTECTION &&
      // Assumption: previous page has to be filled with user input before the user is allowed to visit this page
      store.getState().onboardingInfo.heatingCostsInvoice === ''
    ) {
      navigate(prevPath, { replace: true });
    }
  }, [prevPath, navigate]);

  const [centralHeatingAvailable, setCentralHeatingAvailable] = useState<string>(
    store.getState().onboardingInfo.centralHeatingAvailable
  );

  const radioData = [
    {
      icon: option1Icon,
      key: RadioOptionKeys.CentralHeatingAvailable.Yes,
      trackingId: 'radio-button_yes'
    },
    {
      icon: option2Icon,
      key: RadioOptionKeys.CentralHeatingAvailable.No,
      trackingId: 'radio-button_no'
    }
  ].map((opt) => ({
    icon: opt.icon,
    label: t(translationKeyForRadioOption(opt.key)),
    optionKey: opt.key,
    trackingId: opt.trackingId
  }));

  useHotKey((event: any) => {
    if (event.key === '1') setCentralHeatingAvailable(radioData[0].optionKey);
    if (event.key === '2') setCentralHeatingAvailable(radioData[1].optionKey);
  });

  const canBeSubmitted = () => {
    return centralHeatingAvailable !== '';
  };

  const handlePrevBtnClicked = () => {
    TrackerUtil.trackPrevBtnClick();
    navigate(prevPath);
  };

  const handleSubmit = (e: React.FormEvent) => {
    store.dispatch(setCentralHeatingAvailableInStore(centralHeatingAvailable));

    TrackerUtil.trackNextBtnClick();

    if (
      store.getState().onboardingInfo.centralHeatingAvailable ===
      RadioOptionKeys.CentralHeatingAvailable.Yes
    ) {
      navigate(paths.HotWaterDelivery);
    } else {
      navigate(paths.CentralHeatingMandatory);
    }

    e.preventDefault();
  };

  return (
    <Container>
      <WizardStepsContainer>
        <WizardSteps currentStep={3} totalSteps={6} />
      </WizardStepsContainer>
      <TitleWithPrevPageLink
        onPrevClick={handlePrevBtnClicked}
        title={t('pageCentralHeatingTitle')}
      />
      <PageDescription indented>{t('pageCentralHeatingDescription')}</PageDescription>

      <form onSubmit={handleSubmit}>
        <Row className="justify-content-center mt-4">
          <Col lg={6} md={8}>
            <fieldset>
              <legend id="central-heating-label" className="visually-hidden">
                {t('pageCentralHeatingTitle')}
              </legend>
              <IconRadioGroup
                radioData={radioData}
                handleChange={(newVal: string) => setCentralHeatingAvailable(newVal)}
                currentSelection={() => centralHeatingAvailable}
                aria-labelledby="central-heating-label"
              />
            </fieldset>
          </Col>
        </Row>

        <Row className="justify-content-center py-5">
          <Col className="col-auto">
            <Button
              disabled={!canBeSubmitted()}
              size={SIZE.large}
              type="submit"
              additionalTestId="next"
            >
              {t('nextBtnText')}
            </Button>
          </Col>
        </Row>
      </form>
    </Container>
  );
};

export default CentralHeatingPage;
